import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Facebook, MessageCircle, Copy, HeartHandshake, ExternalLink, HandHelping } from "lucide-react";
import { toast } from "sonner";

// Partage des publications « Entre Voisins » (entraide locale) sur les réseaux.
export default function ProVoisinShare() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    setLoading(true);
    api.get("/entre-voisins")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const shareUrl = (p) => `${ORIGIN}/entre-voisins/${p.id}`;
  const fb = (p) => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(p))}`, "_blank", "noopener,noreferrer");
  const wa = (p) => window.open(`https://wa.me/?text=${encodeURIComponent(`${p.title}\n${shareUrl(p)}`)}`, "_blank", "noopener,noreferrer");
  const copy = async (p) => { try { await navigator.clipboard.writeText(shareUrl(p)); toast.success("Lien copié !"); } catch { toast.info(shareUrl(p)); } };

  return (
    <div data-testid="pro-voisin-share">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
        <HeartHandshake className="text-[#E05638]" size={24} /> Partager une entraide « Entre Voisins »
      </h1>
      <p className="text-slate-500 text-sm mt-1 max-w-2xl">
        Relayez les coups de main et demandes de vos voisins dans vos groupes Facebook, sur WhatsApp ou par lien.
      </p>

      {loading ? <div className="mt-6 text-slate-500 text-sm">Chargement…</div> :
       items.length === 0 ? <div className="mt-6 text-slate-500 text-sm">Aucune publication « Entre Voisins » disponible pour le moment.</div> :
       <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
         {items.map((p) => {
           const photo = p.photo || (p.photos && p.photos[0]) || "";
           return (
             <div key={p.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" data-testid={`pro-share-voisin-${p.id}`}>
               <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                 {photo ? <img src={photo} alt={p.title} className="w-full h-full object-cover" /> :
                   <div className="w-full h-full grid place-items-center text-slate-300"><HandHelping size={32} /></div>}
               </div>
               <div className="p-4 flex-1 flex flex-col">
                 <div className="text-[11px] font-bold uppercase tracking-widest text-rose-700 truncate">
                   {[p.kind === "offer" ? "Je propose" : "Je cherche", p.category, p.city].filter(Boolean).join(" · ")}
                 </div>
                 <h3 className="mt-1 font-heading font-bold text-slate-900 line-clamp-2">{p.title}</h3>
                 {p.description && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{p.description}</p>}
                 <a href={shareUrl(p)} target="_blank" rel="noreferrer" className="mt-2 text-xs text-blue-700 font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`pro-share-voisin-open-${p.id}`}>
                   Voir en ligne <ExternalLink size={11} />
                 </a>
                 <div className="mt-auto pt-3 border-t border-slate-100 flex gap-2">
                   <Button size="sm" onClick={() => fb(p)} className="bg-[#1877F2] hover:bg-[#1465d8] text-white flex-1" data-testid={`pro-share-voisin-fb-${p.id}`}>
                     <Facebook size={14} className="mr-1" /> Facebook
                   </Button>
                   <Button size="sm" variant="outline" onClick={() => wa(p)} className="text-green-700 border-green-300" data-testid={`pro-share-voisin-wa-${p.id}`}><MessageCircle size={14} /></Button>
                   <Button size="sm" variant="outline" onClick={() => copy(p)} data-testid={`pro-share-voisin-copy-${p.id}`}><Copy size={14} /></Button>
                 </div>
               </div>
             </div>
           );
         })}
       </div>}
    </div>
  );
}
